import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

// ─── Config ────────────────────────────────────────────────────────────────
const COUNT    = 9;
const SPEED    = 220;   // world units / second
const TAIL_LEN = 42;    // streak length in world units
const R_MIN    = 350;   // spawn sphere inner radius
const R_MAX    = 750;   // spawn sphere outer radius
const DUR_MIN  = 0.55;  // seconds per streak
const DUR_MAX  = 1.10;

// ─── Helper — fresh shooting-star state ──────────────────────────────────
const mkStar = () => {
  const phi   = Math.acos(2 * Math.random() - 1);
  const theta = Math.random() * Math.PI * 2;
  const r     = R_MIN + Math.random() * (R_MAX - R_MIN);

  const origin = new THREE.Vector3(
    r * Math.sin(phi) * Math.cos(theta),
    r * Math.sin(phi) * Math.sin(theta),
    r * Math.cos(phi),
  );

  const rand = new THREE.Vector3(
    Math.random() - 0.5,
    Math.random() - 0.5,
    Math.random() - 0.5,
  ).normalize();
  const dir = new THREE.Vector3()
    .crossVectors(origin.clone().normalize(), rand)
    .normalize();

  return {
    origin,
    dir,
    elapsed:  -Math.random() * 4.0,
    duration: DUR_MIN + Math.random() * (DUR_MAX - DUR_MIN),
  };
};

// ─── ShootingStars ────────────────────────────────────────────────────────

const ShootingStars = () => {
  const starsState = useRef(Array.from({ length: COUNT }, mkStar));

  // One BufferGeometry per streak — 2 vertices (head + tail)
  const geos = useMemo(() =>
    Array.from({ length: COUNT }, () => {
      const pos = new Float32Array(6);   // 2 × xyz
      const col = new Float32Array(6);   // 2 × rgb
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
      geo.setAttribute("color",    new THREE.BufferAttribute(col, 3));
      return geo;
    }),
  []);

  useFrame((_, delta) => {
    starsState.current.forEach((star, i) => {
      star.elapsed += delta;

      const pos = geos[i].attributes.position.array;
      const col = geos[i].attributes.color.array;

      const progress = star.elapsed / star.duration;

      if (star.elapsed < 0 || progress >= 1) {
        // Keep all zeros → GPU draws degenerate line, nothing visible
        pos.fill(0);
        col.fill(0);
        if (progress >= 1) starsState.current[i] = mkStar();
      } else {
        const dist = star.elapsed * SPEED;
        const hx = star.origin.x + star.dir.x * dist;
        const hy = star.origin.y + star.dir.y * dist;
        const hz = star.origin.z + star.dir.z * dist;

        pos[0] = hx;                       pos[1] = hy;                       pos[2] = hz;
        pos[3] = hx - star.dir.x * TAIL_LEN;
        pos[4] = hy - star.dir.y * TAIL_LEN;
        pos[5] = hz - star.dir.z * TAIL_LEN;

        // Brightness envelope — fade in fast, fade out at end
        const env = Math.sin(progress * Math.PI);

        // Head: bright white
        col[0] = env; col[1] = env; col[2] = env;
        // Tail: black (AdditiveBlending → transparent)
        col[3] = 0;   col[4] = 0;   col[5] = 0;
      }

      geos[i].attributes.position.needsUpdate = true;
      geos[i].attributes.color.needsUpdate    = true;
    });
  });

  return (
    <group>
      {geos.map((geo, i) => (
        <line key={i} geometry={geo}>
          <lineBasicMaterial
            vertexColors
            transparent
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </line>
      ))}
    </group>
  );
};

export default ShootingStars;
